import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Post } from '../types';
import { postsService } from '../services/posts.service';
import { useAuth } from './AuthContext';

const POSTS_CHANGED_EVENT = 'intranet:postsChanged';
const POSTS_POLL_INTERVAL_MS = 60_000;
const INITIAL_LIMIT = 12;

export const emitPostsChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(POSTS_CHANGED_EVENT));
};

export type RefreshPostsOptions = {
  silent?: boolean;
};

type PostsContextType = {
  posts: Post[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  refreshPosts: (options?: RefreshPostsOptions) => Promise<void>;
  loadMorePosts: () => Promise<void>;
  lastUpdatedAt: number | null;
};

const PostsContext = createContext<PostsContextType | undefined>(undefined);

export const PostsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const inFlightRef = useRef(false);
  const loadMoreRef = useRef(false);

  const refreshPosts = useCallback(async (options?: RefreshPostsOptions) => {
    if (!isAuthenticated) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const silent = options?.silent === true;
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const data = await postsService.getPage({ limit: INITIAL_LIMIT });
      if (silent) {
        // Merge: prepend genuinely new posts, update existing ones in-place.
        setPosts((prev) => {
          if (prev.length === 0) return data.items;
          const existingIds = new Set(prev.map((p) => p.id));
          const brandNew = data.items.filter((p) => !existingIds.has(p.id));
          const updatedExisting = prev.map((p) => {
            const fresh = data.items.find((n) => n.id === p.id);
            return fresh ?? p;
          });
          return [...brandNew, ...updatedExisting];
        });
      } else {
        setPosts(data.items);
      }
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
      setLastUpdatedAt(Date.now());
    } catch (e: any) {
      if (!silent) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load posts');
      }
    } finally {
      if (!silent) setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [isAuthenticated]);

  const loadMorePosts = useCallback(async () => {
    if (!isAuthenticated || !hasMore || !nextCursor || loadMoreRef.current) return;
    loadMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const data = await postsService.getPage({ limit: INITIAL_LIMIT, cursor: nextCursor });
      setPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const fresh = data.items.filter((p) => !existingIds.has(p.id));
        return [...prev, ...fresh];
      });
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch {
      // silently ignore — user can retry
    } finally {
      setIsLoadingMore(false);
      loadMoreRef.current = false;
    }
  }, [isAuthenticated, hasMore, nextCursor]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPosts([]);
      setNextCursor(null);
      setHasMore(false);
      setLastUpdatedAt(null);
      setIsLoading(false);
      return;
    }

    // Start loading immediately (no idle-callback delay for first paint).
    void refreshPosts();

    const interval = window.setInterval(() => refreshPosts({ silent: true }), POSTS_POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshPosts({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      inFlightRef.current = false;
    };
  }, [isAuthenticated, refreshPosts]);

  useEffect(() => {
    const handler = () => { refreshPosts({ silent: true }); };
    window.addEventListener(POSTS_CHANGED_EVENT, handler as EventListener);
    return () => window.removeEventListener(POSTS_CHANGED_EVENT, handler as EventListener);
  }, [refreshPosts]);

  const value = useMemo<PostsContextType>(() => ({
    posts,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    refreshPosts,
    loadMorePosts,
    lastUpdatedAt,
  }), [posts, isLoading, isLoadingMore, hasMore, error, refreshPosts, loadMorePosts, lastUpdatedAt]);

  return <PostsContext.Provider value={value}>{children}</PostsContext.Provider>;
};

export const usePosts = (): PostsContextType => {
  const ctx = useContext(PostsContext);
  if (!ctx) throw new Error('usePosts must be used within a PostsProvider');
  return ctx;
};
